import React from 'react';
import './JobTiles.css';

/**
 * 메뉴·옵션 선택 타일 (이름 / 가격 2줄).
 *
 * 래퍼·라벨은 호출하는 쪽 디자인 언어를 따른다 — 홈/모달은 Bootstrap Form.Group,
 * 워크보드는 wf-group/wf-gt. 공유하는 건 타일 자체와 가격 조회 규칙뿐이다.
 */

// 사이즈별 가격 조회. 설정된 가격이 없으면 null (가격 0원과 구분해야 메뉴를 막을 수 있음).
export const getPrice = (item, type, prices, sizeId) => {
    const priceTable = type === 'menu' ? prices?.servicePrices : prices?.optionPrices;
    if (!priceTable || !sizeId) return null;
    const priceEntry = priceTable.find(p =>
        p[type === 'menu' ? 'menu_id' : 'option_id'] === item.id &&
        p.size_id === parseInt(sizeId)
    );
    return priceEntry ? parseFloat(priceEntry.price) : null;
};

/**
 * price 의 세 가지 상태를 구분한다.
 *   undefined → 사이즈 미선택. 가격 줄을 아예 안 그린다.
 *   null      → 그 사이즈에 가격표가 없음. '가격 미설정' 을 경고색으로 보여주되 선택은 막지 않는다.
 *   숫자      → 정상.
 *
 * 선택을 막지 않는 이유: 사이즈 미정으로 저장된 예약을 입고할 때 가격표에 없는 조합이 나올 수 있고
 * (예약 저장 경로는 사이즈를 요구하지 않는다), 그때 이미 선택된 메뉴가 비활성으로 흐려지면
 * 사장님은 이유도 모르고 막힌다. 바쁜 현장에서 가격표 설정으로 보내는 건 최악의 타이밍이라
 * 0원으로라도 접수하고 조정금으로 메울 수 있게 둔다. 대신 조용히 0원이 되지 않도록 표시한다.
 */
const Tile = ({ name, price, prefix = '', selectedClass, selected, onClick }) => (
    <div
        className={`jt-tile ${selected ? selectedClass : ''}`}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
        }}
    >
        <span className="jt-name">{name}</span>
        {price !== undefined && (
            price === null
                ? <span className="jt-price jt-price-missing">가격 미설정</span>
                : <span className="jt-price">{prefix}{price.toLocaleString()}원</span>
        )}
    </div>
);

/**
 * 메뉴 타일 그리드.
 * 사이즈 미선택이면 가격을 숨기고 선택도 막지 않는다 — 모달·워크보드는 같은 폼 안에서
 * 사이즈를 고르므로 메뉴를 먼저 누르는 순서를 허용해야 한다.
 */
export const MenuTiles = ({ menus = [], prices, sizeId, selectedId, onSelect, invalid = false }) => {
    const hasSize = !!sizeId;
    const sel = selectedId ? parseInt(selectedId) : null;
    return (
        <div className={`jt-grid ${invalid ? 'jt-grid-error' : ''}`}>
            {menus.map(menu => {
                const price = getPrice(menu, 'menu', prices, sizeId);
                return (
                    <Tile
                        key={menu.id}
                        name={menu.menuName}
                        price={hasSize ? price : undefined}
                        selected={sel === menu.id}
                        selectedClass="jt-tile-sel"
                        onClick={() => onSelect(menu.id)}
                    />
                );
            })}
        </div>
    );
};

export const OptionTiles = ({ options = [], prices, sizeId, selectedIds = [], onToggle }) => {
    const hasSize = !!sizeId;
    return (
        <div className="jt-grid">
            {options.map(option => {
                const price = getPrice(option, 'option', prices, sizeId);
                return (
                    <Tile
                        key={option.id}
                        name={option.optionName}
                        price={hasSize ? price : undefined}
                        prefix="+"
                        selected={selectedIds.includes(option.id)}
                        selectedClass="jt-tile-opt"
                        onClick={() => onToggle(option.id)}
                    />
                );
            })}
        </div>
    );
};

/**
 * 선택한 메뉴·옵션 중 그 사이즈 가격표에 없는 항목 이름들.
 * 타일이 '가격 미설정' 으로 보여주는 것과 같은 판정(getPrice === null)을 쓴다.
 */
export const findUnpricedItems = ({ menus = [], options = [], prices, sizeId, menuId, optionIds = [] }) => {
    if (!sizeId) return []; // 사이즈 미정은 아직 금액을 확정하는 순간이 아니다 (예약 저장 등)
    const missing = [];
    const menu = menuId ? menus.find(m => m.id === parseInt(menuId)) : null;
    if (menu && getPrice(menu, 'menu', prices, sizeId) === null) missing.push(menu.menuName);
    optionIds.forEach(id => {
        const opt = options.find(o => o.id === id);
        if (opt && getPrice(opt, 'option', prices, sizeId) === null) missing.push(opt.optionName);
    });
    return missing;
};

/**
 * 금액이 확정되는 순간(현장 접수 / 입고 / 완료)에 0원 저장을 조용히 넘기지 않기 위한 확인.
 * 차단이 아니라 확인이다 — 바쁜 현장에서 가격표 설정으로 보내지 않고, 대신 알고 넘어가게 한다.
 * 가격 미설정 항목이 없으면 아무것도 묻지 않고 true.
 */
export const confirmUnpriced = (args) => {
    const missing = findUnpricedItems(args);
    if (missing.length === 0) return true;
    return window.confirm(
        `${missing.join(', ')}\n\n선택한 차량 사이즈의 가격이 설정되지 않았습니다.\n` +
        `위 항목은 0원으로 계산됩니다. 계속하시겠습니까?\n\n` +
        `(설정 > 가격표에서 채울 수 있습니다)`
    );
};
